import React, { useState, useEffect } from "react";
import "./dstyle.css";
import SideBar from "./SideBar";
import Navbar from "./Navbar";
import { API_BASE_URL } from "../../config/api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTrophy,
  faSearch,
  faEnvelope,
  faBookOpen,
  faCertificate,
  faSpinner,
  faAward,
  faChalkboardUser,
  faCheckCircle,
  faFilter
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";

function DCompletions() {
  const navigate = useNavigate();
  const [completions, setCompletions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [scoreFilter, setScoreFilter] = useState("all"); // "all" | "90" | "70"

  useEffect(() => {
    if (!localStorage.getItem("adminId")) { navigate("/admin-login"); return; }
    if (localStorage.getItem("adminRole") !== "main") { navigate("/dashboard"); return; }
    async function fetchCompletionsData() {
      try {
        const [usersRes, coursesRes, assessmentsRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/users`).then((r) => r.json()),
          fetch(`${API_BASE_URL}/api/courses`).then((r) => r.json()),
          fetch(`${API_BASE_URL}/api/assessments`).then((r) => r.json())
        ]);

        const usersList = Array.isArray(usersRes) ? usersRes : [];
        const coursesList = Array.isArray(coursesRes) ? coursesRes : [];
        const assessmentsList = Array.isArray(assessmentsRes) ? assessmentsRes : [];

        // Map and format all assessment records with robust fallbacks
        const map = {};

        assessmentsList.forEach((asm) => {
          const uId = Number(asm.user_id || asm.userId || 0);
          const cId = Number(asm.course_id || asm.courseId || 0);
          const key = `${uId}_${cId}`;
          const currentMarks = Number(asm.marks || 0);

          if (!map[key] || map[key].marks < currentMarks) {
            const user = usersList.find((u) => Number(u.id) === uId);
            const course = coursesList.find((c) => Number(c.course_id || c.id) === cId);

            map[key] = {
              id: asm.id || key,
              userId: uId,
              userName: user?.username || (uId ? `Student #${uId}` : "Enrolled Learner"),
              userEmail: user?.email || (user?.username ? `${user.username.toLowerCase().replace(/\s+/g, '')}@student.com` : "student@lms.com"),
              userPhone: user?.phno || "N/A",
              courseId: cId,
              courseName: course?.course_name || course?.courseName || (cId ? `Course Module #${cId}` : "Course Module"),
              instructor: course?.instructor || course?.tutor || "Lead Specialist",
              marks: currentMarks
            };
          }
        });

        setCompletions(Object.values(map));
        setLoading(false);
      } catch (err) {
        console.error("Error fetching completions:", err);
        setLoading(false);
      }
    }

    fetchCompletionsData();
  }, []);

  const filteredCompletions = completions.filter((item) => {
    // Score Filter
    if (scoreFilter === "90" && item.marks < 90) return false;
    if (scoreFilter === "70" && item.marks < 70) return false;

    // Search Query Filter
    const q = searchTerm.toLowerCase();
    return (
      item.userName.toLowerCase().includes(q) ||
      item.userEmail.toLowerCase().includes(q) ||
      item.courseName.toLowerCase().includes(q) ||
      item.instructor.toLowerCase().includes(q)
    );
  });

  const verifiedCertificatesCount = completions.filter((c) => c.marks >= 90).length;

  return (
    <div style={{ backgroundColor: "#f8fafc", minHeight: "100vh" }}>
      <SideBar current={"completions"} />
      <section id="content">
        <Navbar />
        <main style={{ padding: "28px" }}>

          {/* Header */}
          <div style={{ marginBottom: "28px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "16px" }}>
            <div>
              <h1 style={{ color: "#0f172a", fontFamily: "'Outfit', sans-serif", fontSize: "1.8rem", fontWeight: 800, margin: 0 }}>
                Course Completions & Certifications
              </h1>
              <p style={{ color: "#64748b", margin: "4px 0 0", fontSize: "0.9rem" }}>
                Detailed record of students who completed course modules and assessment quizzes
              </p>
            </div>

            {/* Controls Header: Filter + Search */}
            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", background: "#fff", padding: "4px 12px", borderRadius: "12px", border: "1.5px solid #cbd5e1" }}>
                <FontAwesomeIcon icon={faFilter} style={{ color: "#64748b", fontSize: "0.85rem" }} />
                <select
                  value={scoreFilter}
                  onChange={(e) => setScoreFilter(e.target.value)}
                  style={{ border: "none", outline: "none", background: "transparent", fontSize: "0.85rem", fontWeight: 700, color: "#334155", cursor: "pointer" }}
                >
                  <option value="all">All Submissions</option>
                  <option value="90">Certified (≥90%)</option>
                  <option value="70">Passed (≥70%)</option>
                </select>
              </div>

              <div style={{ position: "relative", minWidth: "260px" }}>
                <FontAwesomeIcon icon={faSearch} style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
                <input
                  type="text"
                  placeholder="Search student or course..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 14px 10px 40px",
                    borderRadius: "12px",
                    border: "1.5px solid #cbd5e1",
                    fontSize: "0.88rem",
                    outline: "none",
                    background: "#fff"
                  }}
                />
              </div>
            </div>
          </div>

          {/* Stat Summary Badges */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "20px", marginBottom: "28px" }}>
            <div style={{ background: "linear-gradient(135deg, #ecfdf5 0%, #ffffff 100%)", borderRadius: "16px", padding: "20px 24px", border: "1.5px solid #bbf7d0", display: "flex", alignItems: "center", gap: "16px" }}>
              <div style={{ width: "48px", height: "48px", borderRadius: "12px", background: "#10b981", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.2rem" }}>
                <FontAwesomeIcon icon={faAward} />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: "1.5rem", fontWeight: 800, color: "#065f46" }}>{verifiedCertificatesCount}</h3>
                <p style={{ margin: 0, color: "#047857", fontSize: "0.82rem", fontWeight: 700 }}>Verified Certificates (≥90%)</p>
              </div>
            </div>

            <div style={{ background: "linear-gradient(135deg, #e0e7ff 0%, #ffffff 100%)", borderRadius: "16px", padding: "20px 24px", border: "1.5px solid #c7d2fe", display: "flex", alignItems: "center", gap: "16px" }}>
              <div style={{ width: "48px", height: "48px", borderRadius: "12px", background: "#4f46e5", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.2rem" }}>
                <FontAwesomeIcon icon={faTrophy} />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: "1.5rem", fontWeight: 800, color: "#3730a3" }}>{completions.length}</h3>
                <p style={{ margin: 0, color: "#4338ca", fontSize: "0.82rem", fontWeight: 700 }}>Total Assessment Submissions</p>
              </div>
            </div>
          </div>

          {/* Completions Table */}
          <div
            style={{
              background: "#fff",
              borderRadius: "20px",
              border: "1px solid #e2e8f0",
              boxShadow: "0 4px 10px rgba(0,0,0,0.03)",
              overflow: "hidden"
            }}
          >
            <div style={{ padding: "18px 24px", borderBottom: "1px solid #f1f5f9", background: "#fafbff", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <h3 style={{ margin: 0, fontWeight: 800, color: "#0f172a", fontSize: "1.05rem", display: "flex", alignItems: "center", gap: "8px" }}>
                <FontAwesomeIcon icon={faCertificate} style={{ color: "#10b981" }} />
                Student Completion Records
              </h3>
              <span style={{ background: "#d1fae5", color: "#059669", padding: "4px 14px", borderRadius: "20px", fontSize: "0.82rem", fontWeight: 700 }}>
                {filteredCompletions.length} Records Shown
              </span>
            </div>

            <div style={{ overflowX: "auto" }}>
              {loading ? (
                <div style={{ textAlign: "center", padding: "50px 0", color: "#64748b" }}>
                  <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: "2rem", color: "#10b981", marginBottom: "12px" }} />
                  <p>Loading completion records...</p>
                </div>
              ) : filteredCompletions.length === 0 ? (
                <div style={{ padding: "40px", textAlign: "center", color: "#64748b" }}>
                  <FontAwesomeIcon icon={faTrophy} style={{ fontSize: "2.2rem", color: "#cbd5e1", marginBottom: "12px" }} />
                  <p style={{ margin: 0, fontWeight: 700 }}>No completion records found matching your filter.</p>
                </div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left", fontSize: "0.9rem" }}>
                  <thead>
                    <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0", color: "#64748b", fontWeight: "700" }}>
                      <th style={{ padding: "14px 24px" }}>Student Details</th>
                      <th style={{ padding: "14px 24px" }}>Contact Email</th>
                      <th style={{ padding: "14px 24px" }}>Completed Course</th>
                      <th style={{ padding: "14px 24px" }}>Instructor</th>
                      <th style={{ padding: "14px 24px", textAlign: "center" }}>Score (%)</th>
                      <th style={{ padding: "14px 24px", textAlign: "center" }}>Certification Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCompletions.map((item) => {
                      const isPassed = item.marks >= 90;
                      return (
                        <tr key={item.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                          <td style={{ padding: "16px 24px", fontWeight: "700", color: "#0f172a" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                              <div
                                style={{
                                  width: "36px",
                                  height: "36px",
                                  borderRadius: "50%",
                                  background: isPassed ? "#d1fae5" : "#e0e7ff",
                                  color: isPassed ? "#059669" : "#4f46e5",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontWeight: "800",
                                  fontSize: "0.9rem"
                                }}
                              >
                                {item.userName.charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <div>{item.userName}</div>
                                <div style={{ fontSize: "0.75rem", color: "#94a3b8", fontWeight: 600 }}>ID #{item.userId}</div>
                              </div>
                            </div>
                          </td>

                          <td style={{ padding: "16px 24px", color: "#334155" }}>
                            <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: "6px", color: "#818cf8" }} />
                            {item.userEmail}
                          </td>

                          <td style={{ padding: "16px 24px", fontWeight: "700", color: "#4f46e5" }}>
                            <FontAwesomeIcon icon={faBookOpen} style={{ marginRight: "6px" }} />
                            {item.courseName}
                          </td>

                          <td style={{ padding: "16px 24px", color: "#475569" }}>
                            <FontAwesomeIcon icon={faChalkboardUser} style={{ marginRight: "6px", color: "#818cf8" }} />
                            {item.instructor}
                          </td>

                          <td style={{ padding: "16px 24px", textAlign: "center" }}>
                            <span
                              style={{
                                background: isPassed ? "#d1fae5" : "#fef3c7",
                                color: isPassed ? "#059669" : "#d97706",
                                padding: "4px 14px",
                                borderRadius: "20px",
                                fontWeight: 800,
                                fontSize: "0.88rem"
                              }}
                            >
                              {item.marks}%
                            </span>
                          </td>

                          <td style={{ padding: "16px 24px", textAlign: "center" }}>
                            {isPassed ? (
                              <span
                                style={{
                                  background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                                  color: "#fff",
                                  padding: "5px 16px",
                                  borderRadius: "20px",
                                  fontSize: "0.78rem",
                                  fontWeight: 700,
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "5px"
                                }}
                              >
                                <FontAwesomeIcon icon={faCheckCircle} /> Verified Certificate
                              </span>
                            ) : (
                              <span
                                style={{
                                  background: "#f1f5f9",
                                  color: "#64748b",
                                  padding: "5px 14px",
                                  borderRadius: "20px",
                                  fontSize: "0.78rem",
                                  fontWeight: 700
                                }}
                              >
                                Assessment Taken
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>

        </main>
      </section>
    </div>
  );
}

export default DCompletions;
